import { Extension, getExtensionField } from '@tiptap/core'

const registrations = new Map()
const usedExtensions = new WeakSet()
const namePattern = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/
const commandPattern = /^[a-z][A-Za-z0-9]*$/
const safeElements = new Set([
  'p', 'br', 'strong', 'b', 'em', 'i', 's', 'u', 'h2', 'h3', 'h4',
  'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'hr', 'img', 'div',
])
const reservedAttribute = /^data-(?:rich-text(?:-|$)|callout$|provider$|placeholder$)/
const dataAttribute = /^data-[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/
const reservedKeys = new Set(['__proto__', 'constructor', 'prototype'])
const reservedCommands = new Set(['run', ...reservedKeys])
const isRecord = (value) => value !== null && typeof value === 'object'
  && [Object.prototype, null].includes(Object.getPrototypeOf(value))

/** Copy plain JSON data; reject executable values, accessors and exotic objects. */
export const copyExtensionData = (value, depth = 0) => {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return value
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (depth > 32) throw new TypeError('RichText extension data exceeds the JSON nesting limit.')
  if (!Array.isArray(value) && !isRecord(value)) {
    throw new TypeError('RichText extension data must be JSON-safe data.')
  }
  if (Object.getOwnPropertySymbols(value).length) {
    throw new TypeError('RichText extension data must not contain symbol keys.')
  }
  const keys = Object.keys(value)
  if (Array.isArray(value) && (keys.length !== value.length
    || keys.some((key, index) => key !== String(index)))) {
    throw new TypeError('RichText extension data must contain only dense JSON arrays.')
  }

  const copy = Array.isArray(value) ? [] : {}
  for (const key of keys.sort()) {
    const descriptor = Object.getOwnPropertyDescriptor(value, key)
    if (reservedKeys.has(key) || !Object.hasOwn(descriptor, 'value')) {
      throw new TypeError('RichText extension data contains a reserved key or accessor.')
    }
    copy[key] = copyExtensionData(descriptor.value, depth + 1)
  }
  return copy
}

/** Register trusted code once, before Alpine initializes editors. This enables nothing. */
export const registerRichTextExtension = (name, factory, configuration = {}) => {
  if (typeof name !== 'string' || !namePattern.test(name) || typeof factory !== 'function') {
    throw new TypeError('RichText extensions require a kebab-case name and a factory function.')
  }
  if (registrations.has(name)) throw new Error(`Duplicate RichText extension registration [${name}].`)
  if (!isRecord(configuration) || Object.keys(configuration).some(key => key !== 'commands')) {
    throw new TypeError('RichText extension registration accepts only a commands list.')
  }
  const { commands = [] } = copyExtensionData(configuration)
  if (!Array.isArray(commands) || commands.some(command =>
    typeof command !== 'string' || !commandPattern.test(command) || reservedCommands.has(command))
    || new Set(commands).size !== commands.length) {
    throw new TypeError('RichText extension commands must be a unique list of command names.')
  }
  registrations.set(name, { factory, commands: [...commands] })
}

export const normalizeEditorExtensions = (definitions = []) => {
  if (!Array.isArray(definitions)) throw new TypeError('RichText extensions must be a list.')
  const names = new Set()
  const attributes = new Set()

  return copyExtensionData(definitions, -2).map((definition) => {
    if (!isRecord(definition) || Object.keys(definition).some(key =>
      !['name', 'attributes', 'options'].includes(key))) {
      throw new TypeError('Malformed RichText extension definition.')
    }
    const { name, attributes: metadata = {}, options = {} } = definition
    if (typeof name !== 'string' || !namePattern.test(name)) {
      throw new TypeError('RichText extension names must use lowercase kebab-case.')
    }
    if (names.has(name)) throw new Error(`Duplicate RichText extension [${name}].`)
    if (!registrations.has(name)) throw new Error(`Unknown RichText extension [${name}]. Register its factory before initialization.`)
    names.add(name)
    // PHP encodes an empty attribute map as []. No other list is a map.
    const normalizedMetadata = Array.isArray(metadata) && metadata.length === 0 ? {} : metadata
    if (!isRecord(normalizedMetadata) || (!isRecord(options) && !Array.isArray(options))) {
      throw new TypeError('RichText extension attributes must be a map and options must be JSON data.')
    }
    for (const [element, granted] of Object.entries(normalizedMetadata)) {
      if (!safeElements.has(element) || !Array.isArray(granted) || granted.length === 0) {
        throw new TypeError('RichText extension metadata requires safe built-in elements and non-empty attribute lists.')
      }
      for (const attribute of granted) {
        if (typeof attribute !== 'string' || !dataAttribute.test(attribute) || reservedAttribute.test(attribute)) {
          throw new TypeError('RichText extensions allow only inert, non-reserved data-* metadata attributes.')
        }
        const key = `${element}.${attribute}`
        if (attributes.has(key)) throw new Error(`Duplicate RichText extension attribute [${key}].`)
        attributes.add(key)
      }
    }
    return { name, attributes: normalizedMetadata, options }
  })
}

export const createEditorExtensions = (definitions, features) => definitions.map(definition => {
  const extension = registrations.get(definition.name).factory({
    ...copyExtensionData(definition, -1), features: [...features],
  })
  if (!(extension instanceof Extension) || extension.type !== 'extension'
    || extension.name !== definition.name || getExtensionField(extension, 'addExtensions')) {
    throw new TypeError(`RichText factory [${definition.name}] must return one new Tiptap Extension with the registered name, without nested extensions.`)
  }
  if (usedExtensions.has(extension)) {
    throw new Error(`RichText factory [${definition.name}] reused an extension instance.`)
  }
  usedExtensions.add(extension)
  return extension
})

/** Validate real Tiptap hooks with their normal context before the view mounts. */
export const extensionCommandAllowlist = (editor, definitions) => {
  const names = new Set()
  const commandOwners = new Map()
  const declared = new Map()
  const attributes = new Set()
  for (const attribute of editor.extensionManager.attributes) {
    const key = `${attribute.type}.${attribute.name}`
    if (attributes.has(key)) throw new Error(`RichText extension schema attribute collision [${key}].`)
    attributes.add(key)
  }
  for (const extension of editor.extensionManager.extensions) {
    if (names.has(extension.name)) throw new Error(`RichText extension name collision [${extension.name}].`)
    names.add(extension.name)
    const addCommands = getExtensionField(extension, 'addCommands', {
      name: extension.name, options: extension.options,
      storage: editor.extensionStorage[extension.name], editor,
      type: editor.schema.nodes[extension.name] || editor.schema.marks[extension.name],
    })
    const commands = addCommands ? addCommands() : {}
    if (!isRecord(commands)) throw new TypeError(`Malformed RichText commands for [${extension.name}].`)
    for (const [command, handler] of Object.entries(commands)) {
      if (reservedCommands.has(command) || commandOwners.has(command)) {
        throw new Error(`RichText extension command collision [${command}].`)
      }
      if (typeof handler !== 'function') throw new TypeError(`Malformed RichText command [${command}].`)
      commandOwners.set(command, extension.name)
    }
  }
  for (const definition of definitions) {
    const commands = registrations.get(definition.name).commands
    for (const command of commands) {
      if (commandOwners.get(command) !== definition.name) {
        throw new Error(`RichText extension [${definition.name}] does not implement declared command [${command}].`)
      }
    }
    declared.set(definition.name, new Set(commands))
  }
  return declared
}
